import React, { useState, useEffect } from 'react';
import { Project, Task } from './types/Project';
import { ProjectForm } from './components/ProjectForm';
import { ProjectInput } from './components/ProjectInput';
import { ProjectList } from './components/ProjectList';
import { ProjectDetail } from './components/ProjectDetail';
import { GanttChart } from './components/GanttChart';
import { EVMDashboard } from './components/EVMDashboard';
import Menu from './components/Menu';
import { loadTemplateData } from './data/templateData';
import './App.css';

function App() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>();
  const [showProjectForm, setShowProjectForm] = useState(false);
  type ViewType = 'list' | 'gantt' | 'detail' | 'evm' | 'project-input';
  const [currentView, setCurrentView] = useState<ViewType>('list');
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // ローカルストレージからデータを読み込み、なければテンプレートデータを使用
  useEffect(() => {
    const savedProjects = localStorage.getItem('progress-management-projects');
    const savedTasks = localStorage.getItem('progress-management-tasks');
    
    if (savedProjects && savedTasks) {
      // 保存されたデータがある場合は復元
      const parsedProjects = JSON.parse(savedProjects).map((p: any) => ({
        ...p,
        startDate: new Date(p.startDate),
        endDate: new Date(p.endDate),
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt)
      }));
      setProjects(parsedProjects);

      const parsedTasks = JSON.parse(savedTasks).map((t: any) => ({
        ...t,
        startDate: new Date(t.startDate),
        endDate: new Date(t.endDate),
        createdAt: new Date(t.createdAt),
        updatedAt: new Date(t.updatedAt)
      }));
      setTasks(parsedTasks);
    } else {
      // 初回起動時はテンプレートデータを使用
      const templateData = loadTemplateData();
      setProjects(templateData.projects);
      setTasks(templateData.tasks);
      
      // テンプレートデータをローカルストレージに保存
      localStorage.setItem('progress-management-projects', JSON.stringify(templateData.projects));
      localStorage.setItem('progress-management-tasks', JSON.stringify(templateData.tasks));
    }
  }, []);

  // データをローカルストレージに保存
  useEffect(() => {
    localStorage.setItem('progress-management-projects', JSON.stringify(projects));
  }, [projects]);

  useEffect(() => {
    localStorage.setItem('progress-management-tasks', JSON.stringify(tasks));
  }, [tasks]);

  // ユーザーメニューの外側をクリックしたときに閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isUserModalOpen && !target.closest('.user-menu-container')) {
        setIsUserModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserModalOpen]);

  const handleCreateProject = (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newProject: Project = {
      ...projectData,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    setProjects(prev => [...prev, newProject]);
    setShowProjectForm(false);
    setCurrentView('list' as ViewType);
  };

  const handleDeleteProject = (id: string) => {
    if (window.confirm('この案件を削除しますか？')) {
      setProjects(prev => prev.filter(p => p.id !== id));
      setTasks(prev => prev.filter(t => t.projectId !== id));
      if (selectedProject?.id === id) {
        setSelectedProject(undefined);
      }
    }
  };

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setCurrentView('detail' as ViewType);
  };

  const handleCreateTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newTask: Task = {
      ...taskData,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    setTasks(prev => [...prev, newTask]);
  };

  const handleUpdateTask = (updatedTask: Task) => {
    setTasks(prev => prev.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    ));
  };

  const handleDeleteTask = (id: string) => {
    if (window.confirm('このタスクを削除しますか？')) {
      setTasks(prev => prev.filter(task => task.id !== id));
    }
  };

  const handleUpdateProject = (updatedProject: Project) => {
    setProjects(prev => prev.map(project => 
      project.id === updatedProject.id ? updatedProject : project
    ));
    setSelectedProject(updatedProject);
  };

  const handleBackToList = () => {
    setCurrentView('list' as ViewType);
    setSelectedProject(undefined);
    setSelectedTaskId(undefined);
  };

  const handleClearSelectedTask = () => {
    setSelectedTaskId(undefined);
  };

  const handleTaskClick = (task: Task) => {
    // タスクが属するプロジェクトを見つけて選択
    const project = projects.find(p => p.id === task.projectId);
    if (project) {
      setSelectedProject(project);
      setSelectedTaskId(task.id);
      setCurrentView('detail' as ViewType);
      // タスク編集のため、プロジェクト詳細画面に遷移
    }
  };

  const handleResetToTemplate = () => {
    if (window.confirm('すべてのデータを削除してテンプレートデータに戻しますか？この操作は取り消せません。')) {
      const templateData = loadTemplateData();
      setProjects(templateData.projects);
      setTasks(templateData.tasks);
      setSelectedProject(undefined);
      setCurrentView('list' as ViewType);
      
      // ローカルストレージもリセット
      localStorage.setItem('progress-management-projects', JSON.stringify(templateData.projects));
      localStorage.setItem('progress-management-tasks', JSON.stringify(templateData.tasks));
    }
  };

  return (
    <div className="App">
      <Menu
        activeTab={currentView === 'detail' ? 'list' : currentView as 'list' | 'gantt' | 'evm' | 'project-input'}
        setActiveTab={(view) => setCurrentView(view as ViewType)}
        isOpen={isMenuOpen}
        onToggle={() => setIsMenuOpen(!isMenuOpen)}
        onNewProject={() => setCurrentView('project-input' as ViewType)}
        onResetToTemplate={handleResetToTemplate}
      />

      <div className={`app-content ${isMenuOpen ? 'menu-open' : 'menu-closed'}`}>
        <header className="app-header">
          <h1>進捗管理システム</h1>
          <div className="header-stats">
            <div className="header-user">
              <div className="user-menu-container">
                <div className="user-info" onClick={() => setIsUserModalOpen((v) => !v)} aria-haspopup="menu" aria-expanded={isUserModalOpen}>
                  <div className="user-details">
                    <span className="user-role">システム管理者</span>
                    <span className="user-caret">{isUserModalOpen ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isUserModalOpen && (
                  <div className="user-menu" role="menu">
                    <div className="user-menu-header">システム太郎</div>
                    <button className="user-menu-item" role="menuitem">マイページ</button>
                    <div className="user-menu-sep" />
                    <button className="user-menu-item" role="menuitem">個人設定</button>
                    <button className="user-menu-item" role="menuitem">パスワード変更</button>
                    <button className="user-menu-item" role="menuitem">メールアドレス変更</button>
                    <button className="user-menu-item" role="menuitem">ソーシャルログイン設定</button>
                    <div className="user-menu-sep" />
                    <button className="user-menu-item danger" role="menuitem" onClick={() => setIsUserModalOpen(false)}>ログアウト</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="app-main">
          {currentView === ('list' as ViewType) ? (
            <ProjectList
              projects={projects}
              onSelectProject={handleSelectProject}
              onDeleteProject={handleDeleteProject}
            />
          ) : currentView === ('project-input' as ViewType) ? (
            <ProjectInput
              onSubmit={handleCreateProject}
              onCancel={() => setCurrentView('list' as ViewType)}
            />
          ) : currentView === ('gantt' as ViewType) ? (
            <GanttChart
              projects={projects}
              tasks={tasks}
              selectedProject={selectedProject}
              onTaskClick={handleTaskClick}
            />
          ) : currentView === ('evm' as ViewType) ? (
            <EVMDashboard
              projects={projects}
              tasks={tasks}
              onBack={handleBackToList}
            />
          ) : currentView === ('detail' as ViewType) && selectedProject ? (
            <ProjectDetail
              project={selectedProject}
              tasks={tasks}
              onBack={handleBackToList}
              onCreateTask={handleCreateTask}
              onUpdateTask={handleUpdateTask}
              onDeleteTask={handleDeleteTask}
              onUpdateProject={handleUpdateProject}
              selectedTaskId={selectedTaskId}
              onClearSelectedTask={handleClearSelectedTask}
            />
          ) : null}
        </main>

        {showProjectForm && (
          <ProjectForm
            onSubmit={handleCreateProject}
            onCancel={() => setShowProjectForm(false)}
          />
        )}
      </div>
    </div>
  );
}

export default App;